import { assertNever } from '@tableau/data-structures';
import { FieldDataType, FieldIconFragment, FieldRoleCategory, Maybe, NonDatasourceFieldIconFragment, NonDatasourceFieldIcon_BinField_Fragment, NonDatasourceFieldIcon_CalculatedField_Fragment, NonDatasourceFieldIcon_ColumnField_Fragment, NonDatasourceFieldIcon_GroupField_Fragment } from '../../../../../../gen-src/queries';
import { FieldIcon } from '../types/FieldIcon';

export const geographicalSemanticRolePrefix = '[Geographical]';

export function isContinuous(field: { dataCategory: FieldRoleCategory | null }): boolean {
  // NOMINAL and ORDINAL are discrete; QUANTITATIVE is continuous.
  // If UNKNOWN, default to discrete.
  return field.dataCategory === FieldRoleCategory.QUANTITATIVE;
}

export function isGeographical(fieldMaybeWithRole: { semanticRole: string | null } | unknown): boolean {
  const fieldWithRole = fieldMaybeWithRole as { semanticRole: string | null };
  return !!fieldWithRole.semanticRole && fieldWithRole.semanticRole.startsWith(geographicalSemanticRolePrefix);
}

export function iconForBinField(field: NonDatasourceFieldIcon_BinField_Fragment): FieldIcon | null {
  if (field.dataCategory === null) {
    return null;
  }

  return isContinuous(field) ? FieldIcon.NumericBinContinuous : FieldIcon.NumericBinDiscrete;
}

export function iconForCalculatedField(field: NonDatasourceFieldIcon_CalculatedField_Fragment): FieldIcon | null {
  if (isGeographical(field)) {
    return isContinuous(field) ? FieldIcon.GeoCalcContinuous : FieldIcon.GeoCalcDiscrete;
  } else if (field.dataCategory === null) {
    return null;
  } else {
    switch (field.dataType) {
      case FieldDataType.BOOLEAN:
        // Bools are always discrete.
        return FieldIcon.BoolCalc;
      case FieldDataType.DATE:
        return isContinuous(field) ? FieldIcon.DateCalcContinuous : FieldIcon.DateCalcDiscrete;
      case FieldDataType.DATETIME:
        return isContinuous(field) ? FieldIcon.DateTimeCalcContinuous : FieldIcon.DateTimeCalcDiscrete;
      case FieldDataType.INTEGER:
      case FieldDataType.REAL:
      case FieldDataType.TABLE:
        return isContinuous(field) ? FieldIcon.NumberCalcContinuous : FieldIcon.NumberCalcDiscrete;
      case FieldDataType.SPATIAL:
        // Spatial fields display as geographical.
        return isContinuous(field) ? FieldIcon.GeoCalcContinuous : FieldIcon.GeoCalcDiscrete;
      case FieldDataType.STRING:
        return isContinuous(field) ? FieldIcon.TextCalcContinuous : FieldIcon.TextCalcDiscrete;
      case FieldDataType.TUPLE:
        // Not sure what this means, and there's no obvious icon for it.
        return null;
      case FieldDataType.UNKNOWN:
      case null:
        return null;
      default:
        assertNever(field.dataType);
        return null;
    }
  }
}

export function iconForColumnField(field: NonDatasourceFieldIcon_ColumnField_Fragment): FieldIcon | null {
  if (isGeographical(field)) {
    return isContinuous(field) ? FieldIcon.GeoContinuous : FieldIcon.GeoDiscrete;
  } else if (field.dataCategory === null) {
    return null;
  } else {
    switch (field.dataType) {
      case FieldDataType.BOOLEAN:
        // Bools are always discrete.
        return FieldIcon.Bool;
      case FieldDataType.DATE:
        return isContinuous(field) ? FieldIcon.DateContinuous : FieldIcon.DateDiscrete;
      case FieldDataType.DATETIME:
        return isContinuous(field) ? FieldIcon.DateTimeContinuous : FieldIcon.DateTimeDiscrete;
      case FieldDataType.INTEGER:
      case FieldDataType.REAL:
      case FieldDataType.TABLE:
        return isContinuous(field) ? FieldIcon.NumberContinuous : FieldIcon.NumberDiscrete;
      case FieldDataType.SPATIAL:
        // Spatial fields display as geographical.
        return isContinuous(field) ? FieldIcon.GeoContinuous : FieldIcon.GeoDiscrete;
      case FieldDataType.STRING:
        return isContinuous(field) ? FieldIcon.TextContinuous : FieldIcon.TextDiscrete;
      case FieldDataType.TUPLE:
        // Not sure what this means, and there's no obvious icon for it.
        return null;
      case FieldDataType.UNKNOWN:
      case null:
        return null;
      default:
        assertNever(field.dataType);
        return null;
    }
  }
}

export function iconForGroupField(field: NonDatasourceFieldIcon_GroupField_Fragment): FieldIcon {
  if (field.fieldsReferencedByGroup.length > 0 && isGeographical(field.fieldsReferencedByGroup[0])) {
    return FieldIcon.GroupGeo;
  } else {
    return FieldIcon.Group;
  }
}

export function iconForNonDatasourceField(field: NonDatasourceFieldIconFragment): FieldIcon | null {
  switch (field.__typename) {
    case 'BinField':
      return iconForBinField(field);
    case 'CalculatedField':
      return iconForCalculatedField(field);
    case 'ColumnField':
      return iconForColumnField(field);
    case 'CombinedField':
      // Combined fields (that are not combined set fields) are always text.
      return FieldIcon.TextDiscrete;
    case 'CombinedSetField':
      // Combined set fields display as sets.
      return FieldIcon.Set;
    case 'DatasourceField':
      // Don't expect datasource fields here; they're handled by iconForField.
      return null;
    case 'GroupField':
      return iconForGroupField(field);
    case 'HierarchyField':
      return FieldIcon.Hierarchy;
    case 'SetField':
      return FieldIcon.Set;
    default:
      assertNever(field);
      return null;
  }
}

export function iconForField(field: Maybe<FieldIconFragment>): FieldIcon | null {
  if (!field) {
    return null;
  }

  if (field.__typename !== 'DatasourceField') {
    return iconForNonDatasourceField(field);
  } else if (field.remoteField) {
    return iconForNonDatasourceField(field.remoteField);
  } else {
    return null;
  }
}
